/**
 * Displaying submission data
 */

import Vue from 'vue';
import { getMutationsForScope, getActionsForScope } from './../utils';
import {
  errors,
  SUBMISSIONS_BUFFER_SIZE,
  SUBMISSIONS_PAGE_SIZE,
  ROUTE_NAMES,
  ROUTE_QUERY_PARAMS,
} from './../../../core/config';
import api from './../../../core/api';
import router from './../../../core/router';
import iframeChannel from './../../../core/iframe-channel';

const mutation = getMutationsForScope('submissionView');
const action = getActionsForScope('submissionView');

const moduleState = {
  lightboxExpanded: false, // is the submission lightbox expanded,
  activeSubmissionId: null, // submission that has been focused,
  lastCall: null,
  lastPageCall: null,
  moreSubmissions: true,
  submissionsLoaded: false,
  error: null,
  submissionsVisible: [], // array of visisble submission for carousel type interaction in lightbox
  submissionsMap: {}, // the submissions mapped for easy lookup
  activePageIndex: 0,
  pageSize: SUBMISSIONS_PAGE_SIZE,
  nextResultsPage: 1,
  reportedThisSession: [], // ids that have been reported this visit

  // fake submissions for checking messages
  // submissionsMap: {
  //   pending: {
  //     src: videoDemo,
  //     id: 'pending',
  //     thumbnail: {
  //       url: thumbnailDemo,
  //     },
  //     category: SUBMISSION_CATEGORIES.video,
  //     status: submissionStatuses.PENDING,
  //     aspect_ratio: '600:600'
  //   },
  //   flagged: {
  //     src: thumbnailDemo,
  //     id: 'flagged',
  //     thumbnail: {
  //       url: thumbnailDemo,
  //     },
  //     category: SUBMISSION_CATEGORIES.image,
  //     status: submissionStatuses.FLAGGED,
  //   },
  //   rejected: {
  //     src: thumbnailDemo,
  //     id: 'rejected',
  //     thumbnail: {
  //       url: thumbnailDemo
  //     },
  //     category: SUBMISSION_CATEGORIES.image,
  //     status: submissionStatuses.REJECTED,
  //   },
  //   approved: {
  //     image: {
  //       url: thumbnailDemo
  //     },
  //     id: 'approved',
  //     thumbnail: {
  //       url: thumbnailDemo
  //     },
  //     category: SUBMISSION_CATEGORIES.image,
  //     status: submissionStatuses.APPROVED,
  //   },
  // },
};

const moduleGetters = {
  submissionsLength(state) {
    return state.submissionsVisible.length;
  },
  noSubmissions(state, getters) {
    return getters.submissionsLength < 1;
  },
  activeSubmission(state) {
    return state.submissionsMap[state.activeSubmissionId];
  },

  previousSubmissionIndex(state, getters) {
    return getters.activeSubmissionIndex - 1;
  },

  canPreviousSubmission(state, getters) {
    return getters.previousSubmissionIndex >= 0;
  },

  previousSubmissionId(state, getters) {
    if (!getters.canPreviousSubmission) {
      return null;
    }
    return state.submissionsVisible[getters.previousSubmissionIndex];
  },

  previousSubmission(state, getters) {
    if (!getters.previousSubmissionId) {
      return null;
    }

    return state.submissionsMap[getters.previousSubmissionId];
  },

  nextSubmissionIndex(state, getters) {
    return getters.activeSubmissionIndex + 1;
  },

  canNextSubmission(state, getters) {
    return state.submissionsVisible.length > getters.nextSubmissionIndex;
  },

  nextSubmissionId(state, getters) {
    if (!getters.canNextSubmission) {
      return null;
    }
    return state.submissionsVisible[getters.nextSubmissionIndex];
  },

  nextSubmission(state, getters) {
    if (!getters.nextSubmissionId) {
      return null;
    }

    return state.submissionsMap[getters.nextSubmissionId];
  },

  activePage(state, getters) {
    return state.submissionsVisible.slice(getters.activePageStart, getters.activePageEnd);
  },

  activePageStart(state) {
    return state.activePageIndex * state.pageSize;
  },

  activePageEnd(state, getters) {
    return getters.activePageStart + state.pageSize;
  },

  nextPageIndex(state) {
    return state.activePageIndex + 1;
  },

  canNextPage(state, getters) {
    return getters.totalPages > getters.nextPageIndex;
  },

  previousPageIndex(state) {
    return state.activePageIndex - 1;
  },
  canPreviousPage(state, getters) {
    return getters.previousPageIndex >= 0;
  },

  totalPages(state) {
    return Math.ceil(state.submissionsVisible.length / state.pageSize);
  },

  activeSubmissionIndex(state, getters) {
    let index = -1;

    if (getters.activeSubmission) {
      index = state.submissionsVisible.indexOf(getters.activeSubmission.id);
    }

    // if (index < 0) {
    //   index = 0;
    // }

    return index;
  },

  activeSubmissionPage(state, getters) {
    let page = Math.ceil(getters.activeSubmissionIndex / state.pageSize);

    if (page > 0) {
      page -= 1; // zero base
    }
    return page;
  },

  bufferLow(state, getters) {
    // active submission too close
    let out = getters.submissionsLength - getters.activeSubmissionIndex <= SUBMISSIONS_BUFFER_SIZE;

    if (!out) {
      // check active page
      out = getters.submissionsLength - state.activePageIndex * state.pageSize <= SUBMISSIONS_BUFFER_SIZE;
    }

    console.log(out);
    return out;
  },

  /**
   * Root of the submissions so that we can scroll to it
   *
   * @returns
   */
  submissionViewRoot() {
    return window.document.getElementById('submissions');
  }
};

const mutations = {
  /**
   * set the focused submission
   * @param {*} state
   * @param {*} submission - the submission being focused
   */
  [mutation.SUBMISSION_VIEW_SET_ACTIVE_SUBMISSION_ID](state, { submissionId }) {
    state.activeSubmissionId = submissionId;
  },

  /**
   * clear the focused submission
   * @param {*} state
   */
  [mutation.SUBMISSION_VIEW_CLEAR_ACTIVE_SUBMISSION_ID](state) {
    state.activeSubmissionId = null;
  },

  /**
   * set the lightbox to open or close.
   * @param {*} state
   * @param {Boolean} open
   */
  [mutation.SUBMISSION_VIEW_SET_LIGHTBOX_EXPANDED](state, { open }) {
    state.lightboxExpanded = open;
  },

  /**
   * update the submissions visible in the grid
   *
   * @param {*} state
   * @param {*} payload { submissionIds }
   * @param {Array.<String>} payload.submissionIds }
   */
  [mutation.SUBMISSION_VIEW_UPDATE_SUBMISSIONS_VISIBLE](state, { submissionIds }) {
    const newIds = submissionIds.filter(id => state.submissionsVisible.indexOf(id) < 0);

    state.submissionsVisible = state.submissionsVisible.concat(newIds);
  },

  /**
   * update the map of all submissions loaded, irregardless of status
   *
   * @param {*} state
   * @param {*} { submissionsMap }
   */
  [mutation.SUBMISSION_VIEW_UPDATE_SUBMISSIONS_MAP](state, { submission }) {
    Vue.set(state.submissionsMap, submission.id, submission);
  },

  /**
   * Set the active page
   *
   * @param {*} state
   * @param {*} { pageIndex }
   */
  [mutation.SUBMISSION_VIEW_SET_ACTIVE_PAGE_INDEX](state, { pageIndex }) {
    state.activePageIndex = pageIndex;
  },

  /**
   * Set if there are more submissions
   *
   * @param {*} state
   * @param {*} { moreSubmissions }
   */
  [mutation.SUBMISSION_VIEW_SET_MORE_SUBMISSIONS](state, { moreSubmissions }) {
    state.moreSubmissions = moreSubmissions;
  },

  /**
   * Set last call reference
   *
   * @param {*} state
   * @param {*} payload
   * @param {GrowBaseAPI~Call} payload.call
   */
  [mutation.SUBMISSION_VIEW_SET_LAST_CALL](state, { call }) {
    state.lastCall = call;
  },

  /**
   * Set last call reference
   *
   * @param {*} state
   * @param {*} payload
   * @param {GrowBaseAPI~Call} payload.call
   */
  [mutation.SUBMISSION_VIEW_SET_LAST_PAGE_CALL](state, { call }) {
    state.lastPageCall = call;
  },

  /**
   * Set error
   *
   * @param {*} state
   * @param {*} payload
   * @param {Error} payload.error { error }
   */
  [mutation.SUBMISSION_VIEW_SET_ERROR](state, { error }) {
    state.error = error;
  },

  /**
   * Set the next page to get of results
   *
   * @param {*} state
   * @param {*} { next }
   */
  [mutation.SUBMISSION_VIEW_SET_NEXT_RESULTS_PAGE](state, { next }) {
    state.nextResultsPage = next;
  },

  /**
   * Set submission as reported
   *
   * @param {*} state
   * @param {*} { submissionId }
   */
  [mutation.SUBMISSION_VIEW_SET_SUBMISSION_REPORTED](state, { submissionId }) {
    state.reportedThisSession = state.reportedThisSession.concat(submissionId);
  }
};

const actions = {
  /**
   * Get the next page of submissions
   *
   * @param {*} {
   *     state,
   *     getters,
   *     commit,
   *     dispatch,
   *   }
   * @param {*} [{
   *     checkBuffer = false, // whether to check the buffer before fetching
   *   }={}]
   * @returns
   */
  async [action.SUBMISSION_VIEW_GET_SUBMISSIONS]({
    state,
    getters,
    commit,
    dispatch,
  },
  {
    checkBuffer = false, // whether to check the buffer before fetching
  } = {}) {
    if (!getters.bufferLow && checkBuffer) {
      return null;
    }

    if (!state.moreSubmissions || !state.nextResultsPage) {
      // Should this be an error to be consistent? - AH

      return null;
    }
    if (state.lastPageCall) {
      // cancel last call

      await dispatch(action.SUBMISSION_VIEW_CANCEL, { what: 'lastPageCall' });
    }

    // try to fetch more submissions
    // page numbers start at 1, not 0
    const pageCall = api.getSubmissions(state.nextResultsPage, state.pageSize);

    // save reference for cancelling
    commit(mutation.SUBMISSION_VIEW_SET_LAST_CALL, { pageCall });

    return pageCall.promise.then((response) => {
      // get the share url {
      if (!response.success && !response.data) {
        const error = new Error(errors.SUBMISSION_VIEW_SERVER_ERROR);
        error.response = response;
        throw error;
      }

      // clear errors
      // handle sucess
      const { data } = response;

      commit(mutation.SUBMISSION_VIEW_SET_ERROR, {});

      const {
        submissions,
        pagination: {
          next,
        }
      } = data;

      const moreSubmissions = !!next;

      if (!submissions.length) {
        // no submissions returned, assume we should stop asking
        commit(mutation.SUBMISSION_VIEW_SET_MORE_SUBMISSIONS, { moreSubmissions: false });

        // if no submissions are returned, reject with errors.SUBMISSIONS_NONE
        const error = new Error(errors.SUBMISSIONS_NONE);
        throw error;
      }

      // update if there are more submissionsx `state.moreSubmissions` to false
      commit(mutation.SUBMISSION_VIEW_SET_MORE_SUBMISSIONS, { moreSubmissions });
      commit(mutation.SUBMISSION_VIEW_SET_NEXT_RESULTS_PAGE, { next });

      console.log(data, moreSubmissions);

      // clear call
      commit(mutation.SUBMISSION_VIEW_SET_LAST_PAGE_CALL, {
        call: null,
      });


      return dispatch(action.SUBMISSION_VIEW_HANDLE_SUBMISSIONS, { submissions })
        .then(() => {
          if (getters.bufferLow) {
            return dispatch(action.SUBMISSION_VIEW_GET_SUBMISSIONS);
          }
          return null;
        });
    })
      .catch((error) => {
        error.message = error.message === errors.SUBMISSIONS_NONE
          ? error.message
          : errors.SUBMISSION_SERVER_ERROR;

        // if no submissions are returned, set `state.moreSubmissions` to false
        commit(mutation.SUBMISSION_VIEW_SET_MORE_SUBMISSIONS, { moreSubmissions: false });
        if (error.message === errors.SUBMISSIONS_NONE && state.activeSubmissionId) {
          return dispatch(action.SUBMISSION_VIEW_RESET_ACTIVE_SUBMISSION);
        }
        return dispatch(action.SUBMISSION_VIEW_HANDLE_ERROR, {
          error,
        });
      });
  },

  /**
   * Request the submission with submission id
   *
   * @param {*} {
   *       state,
   *       commit,
   *       dispatch,
   *       state: {
   *         lastCall
   *       }
   *     }
   * @param {*} {
   *       submissionId
   *     }
   * @returns
   */
  async [action.SUBMISSION_VIEW_GET_SUBMISSION](
    {
      state,
      commit,
      dispatch,
      state: {
        lastCall
      }
    },
    {
      submissionId
    }
  ) {
    if (state.submissionsMap[submissionId]) {
      // already loaded submission, update ui
      console.log('submission loaded', submissionId);

      return dispatch(action.SUBMISSION_VIEW_HANDLE_SUBMISSION_FOUND, { submissionId });
    }

    if (lastCall) {
      // cancel last call

      await dispatch(action.SUBMISSION_VIEW_CANCEL, { what: 'call' });
    }

    // make a new call
    const call = api.getSubmission(
      submissionId,
    );

    // save reference for cancelling
    commit(mutation.SUBMISSION_VIEW_SET_LAST_CALL, { call });

    return call.promise.then((response) => {
      // get the share url {
      if (!response.success && !response.data) {
        const error = new Error(errors.SUBMISSION_VIEW_SERVER_ERROR);
        error.response = response;
        throw error;
      }

      // clear errors
      // handle sucess
      const { data } = response;

      commit(mutation.SUBMISSION_VIEW_SET_ERROR, {});

      const responseId = data.submission && data.submission.id;

      // clear call
      commit(mutation.SUBMISSION_VIEW_SET_LAST_CALL, {
        call: null,
      });

      if (!responseId) {
        return dispatch(action.SUBMISSION_VIEW_HANDLE_SUBMISSION_NOT_FOUND);
      }

      commit(mutation.SUBMISSION_VIEW_UPDATE_SUBMISSIONS_MAP, { submission: data.submission });

      return dispatch(action.SUBMISSION_VIEW_HANDLE_SUBMISSION_FOUND, { submissionId: responseId });
    })
      .catch((error) => {
        error.message = errors.SUBMISSION_SERVER_ERROR;

        return Promise.all([
          dispatch(action.SUBMISSION_VIEW_HANDLE_SUBMISSION_NOT_FOUND),
          dispatch(action.SUBMISSION_VIEW_HANDLE_ERROR, {
            error,
          })
        ]);
      });
  },

  async [action.SUBMISSION_VIEW_REPORT_SUBMISSION](
    { commit },
    { submissionId }
  ) {
    // Update the UI immediately irregardless of if this works so that the spleen can be vented
    commit(mutation.SUBMISSION_VIEW_SET_SUBMISSION_REPORTED, { submissionId });

    const call = api.reportSubmission(submissionId);

    call.promise.catch((error) => {
      // Thanks for trying.

      console.log(error);
    });
  },

  /**
   * Cancel the existing call.
   *
   * @param {*} context cancel existing call so we can make another one
   * @returns {Promise.<null>} resolves when call is over and gone
   */
  [action.SUBMISSION_VIEW_CANCEL]({
    commit,
    state: {
      lastCall,
      lastPageCall
    }
  }, {
    what
  } = {}) {
    const call = what === 'pageCall' ? lastPageCall : lastCall;

    if (call) {
      // FIXME: illegal invocation
      console.log(call.request);
      call.abort();
    }

    if (what === 'pageCall') {
      commit(mutation.SUBMISSION_VIEW_SET_LAST_CALL, { call: null });
      return null;
    }
    commit(mutation.SUBMISSION_VIEW_SET_LAST_CALL, { call: null });


    return null;
  },

  /**
   * Clear the submission store on error
   *
   * @param {*} context
   * @param {*} payload
   * @param {Error} payload.error
   * @param {Error} [payload.error.response] response from server
   */
  [action.SUBMISSION_VIEW_HANDLE_ERROR]({
    commit,
    dispatch
  },
  {
    error
  }) {
    // save error
    commit(mutation.SUBMISSION_VIEW_SET_ERROR, { error });

    // clear last call
    return dispatch(action.SUBMISSION_VIEW_CANCEL);
  },

  /**
   * If the submission is found, set the active submissions and clear the lightbox
   *
   * @param {*} {
   *     commit,
   *   }
   * @param {*} {
   *     submissionId
   *   }
   */
  [action.SUBMISSION_VIEW_HANDLE_SUBMISSION_FOUND]({
    commit,
  }, {
    submissionId
  }) {
    // set the active submission
    commit(mutation.SUBMISSION_VIEW_SET_ACTIVE_SUBMISSION_ID, { submissionId });
    // Open the lightbox
    commit(mutation.SUBMISSION_VIEW_SET_LIGHTBOX_EXPANDED, { open: true });
  },

  /**
   * If the requested submission is not found, open the clear the active submission and open the lightbox
   *
   * This is separate because it seems like it should be, event though it is the same as success - AH
   *
   * @param {*} {
   *     commit,
   *   }
   * @param {*} {
   *     submissionId
   *   }
   */
  [action.SUBMISSION_VIEW_HANDLE_SUBMISSION_NOT_FOUND]({
    commit,
  },
  {
    submissionId
  } = {}) {
    // set the active submission
    commit(mutation.SUBMISSION_VIEW_SET_ACTIVE_SUBMISSION_ID, { submissionId });

    // Open the lightbox
    commit(mutation.SUBMISSION_VIEW_SET_LIGHTBOX_EXPANDED, { open: true });
  },

  /**
   * Reset the active submision to the first one.
   *
   * @param {*} {
   *     commit,
   *     state
   *   }
   * @returns
   */
  [action.SUBMISSION_VIEW_RESET_ACTIVE_SUBMISSION]({
    commit,
    state
  }) {
    if (state.activeSubmissionId) {
      return;
    }
    const submissionId = state.submissionsVisible[0];

    if (!state.lightboxExpanded) {
      // error message...
      commit(mutation.SUBMISSION_VIEW_SET_ACTIVE_SUBMISSION_ID, { submissionId });
    }
  },

  /**
   * Normalize the submissions for display
   *
   * @param {*} {
   *     commit,
   *     dispatch
   *   }
   * @param {*} [{
   *     submissions = []
   *   }={}]
   * @returns
   */
  [action.SUBMISSION_VIEW_HANDLE_SUBMISSIONS]({
    commit,
    dispatch
  },
  {
    submissions = []
  } = {}) {
    // if more submissions are returned, update visible submissions
    const submissionIds = [];

    submissions.forEach((submission) => {
      const id = `${submission.id}`; // force string
      submissionIds.push(id);
      commit(mutation.SUBMISSION_VIEW_UPDATE_SUBMISSIONS_MAP, { submission });
    });

    commit(mutation.SUBMISSION_VIEW_UPDATE_SUBMISSIONS_VISIBLE, {
      submissionIds
    });

    // update parent height
    Vue.nextTick(iframeChannel.forceResize());

    return dispatch(action.SUBMISSION_VIEW_RESET_ACTIVE_SUBMISSION);
  },

  /**
   * Advance the ui's active Submission in the given direction
   *
   * @param {*} {
   *     getters,
   *     commit,
   *     dispatch,
   *     state,
   *   }
   * @param {*} {
   *     direction
   *   }
   * @returns
   */
  async [action.SUBMISSION_VIEW_STEP_SUBMISSION]({
    getters,
    commit,
    dispatch,
    state,
  },
  {
    direction,
    navigate,
  }) {
    const canStep = (direction === 'next') ? getters.canNextSubmission : getters.canPreviousSubmission;

    if (!canStep) {
      // buffer will have already taken care of fetching more
      return null;
    }

    const submissionId = (direction === 'next') ? getters.nextSubmissionId : getters.previousSubmissionId;

    commit(mutation.SUBMISSION_VIEW_SET_ACTIVE_SUBMISSION_ID, {
      submissionId,
    });

    // update the url
    if (navigate) {
      await dispatch(action.SUBMISSION_VIEW_UPDATE_SUBMISSION_PARAM, { submissionId });
    }

    if (getters.activeSubmissionPage !== state.activePageIndex && getters.activeSubmissionIndex > -1) {
      // we're not on the page of the activeSubmission, update
      commit(mutation.SUBMISSION_VIEW_SET_ACTIVE_PAGE_INDEX, {
        pageIndex: getters.activeSubmissionPage,
      });

      Vue.nextTick().then(() => {
        iframeChannel.scrollTo({
          target: getters.submissionViewRoot,
          noScroll: true,
        });
      });
    }

    if (direction !== 'next') {
      return null;
    }

    return dispatch(action.SUBMISSION_VIEW_GET_SUBMISSIONS, {
      checkBuffer: true
    });
  },

  /**
   * Advance the ui's current page in the given direction
   *
   * @param {*} {
   *     getters,
   *     commit,
   *     dispatch,
   *   }
   * @param {*} {
   *     direction
   *   }
   * @returns
   */
  [action.SUBMISSION_VIEW_STEP_PAGE]({
    getters,
    commit,
    dispatch,
  },
  {
    direction
  }) {
    const canStep = (direction === 'next') ? getters.canNextPage : getters.canPreviousPage;

    if (!canStep) {
      // buffer will have already taken care of fetching more
      return null;
    }

    const pageIndex = (direction === 'next') ? getters.nextPageIndex : getters.previousPageIndex;

    commit(mutation.SUBMISSION_VIEW_SET_ACTIVE_PAGE_INDEX, {
      pageIndex,
    });

    if (direction !== 'next') {
      return null;
    }

    Vue.nextTick().then(() => {
      iframeChannel.scrollTo({
        target: getters.submissionViewRoot,
        noScroll: true,
      });
    });

    return dispatch(action.SUBMISSION_VIEW_GET_SUBMISSIONS, {
      checkBuffer: true
    });
  },

  /**
   * Update the URL for debugging purposes
   *
   * @param {*} context
   * @param {*} {
   *     submissionId
   *   }
   * @returns
   */
  [action.SUBMISSION_VIEW_UPDATE_SUBMISSION_PARAM](context, {
    submissionId
  }) {
    const onHome = router.currentRoute.name === ROUTE_NAMES.home;

    if (!onHome) {
      return;
    }

    if (submissionId) {
      iframeChannel.updateRoute({
        type: 'push',
        route: {
          name: ROUTE_NAMES.home,
          query: {
            [ROUTE_QUERY_PARAMS.submissionId]: submissionId,
          }
        }
      });
      return;
    }

    iframeChannel.updateRoute({
      type: 'push',
      route: {
        name: ROUTE_NAMES.home,
        query: {
          [ROUTE_QUERY_PARAMS.submissionId]: '',
        }
      }
    });
  }
};

export default {
  namespaced: true,
  state: moduleState,
  getters: moduleGetters,
  mutations,
  actions,
};
